const TAU = Math.PI * 2;

/* ---------- Utilities ---------- */

function smallestSignedAngleBetween(target, source) {
  return Math.atan2(Math.sin(target - source), Math.cos(target - source));
}

function mod(n, m) {
  return ((n % m) + m) % m;
}

function normaliseAngle(angle) {
  return mod(angle + Math.PI, TAU) - Math.PI;
}

function polarToCartesian(angle, radius) {
  return {
    x: radius * Math.cos(angle),
    y: radius * Math.sin(angle),
  };
}

function getPointerLocation(evt, canvas) {
  const rect = canvas.getBoundingClientRect();
  return {
    x: evt.clientX - rect.left,
    y: evt.clientY - rect.top,
  };
}

function generateDataFromProportions(proportions) {
  const total = proportions.reduce((a, v) => a + v.proportion, 0);
  let currentAngle = 0;

  return proportions.map((v) => {
    const arcSize = (TAU * v.proportion) / total;
    const data = {
      angle: currentAngle,
      format: v.format,
      collapsed: arcSize <= 0,
    };
    currentAngle = normaliseAngle(currentAngle + arcSize);
    return data;
  });
}

const DEFAULT_RADIUS = 0.9;
const DEFAULT_COLLAPSING = false;
const DEFAULT_MIN_ANGLE = 0.1;

const defaultDrawSegment = (
  context, _piechart, centerX, centerY, radius, startingAngle, arcSize, format, collapsed
) => {
  if (collapsed) return;
  const endingAngle = startingAngle + arcSize;

  context.save();
  context.beginPath();
  context.moveTo(centerX, centerY);
  context.arc(centerX, centerY, radius, startingAngle, endingAngle, false);
  context.closePath();
  context.fillStyle = format.color;
  context.fill();
  context.restore();
};

const defaultDrawNode = (context, _piechart, x, y, centerX, centerY, hover) => {
  context.save();
  context.translate(centerX, centerY);
  context.fillStyle = "#DDDDDD";
  const rad = hover ? 7 : 5;
  context.beginPath();
  context.arc(x, y, rad, 0, TAU, true);
  context.fill();
  context.stroke();
  context.restore();
};

/* ---------- Core Class ---------- */

export class DraggablePiechart {
  handlePointerDown = (event) => {
    event.preventDefault?.();
    if (this.activePointerId !== null) return;

    const dragTarget = this.getTarget(getPointerLocation(event, this.canvas));
    if (!dragTarget) return; // ✅ jangan lock pointer kalau nggak kena node

    this.activePointerId = event.pointerId;
    try { this.canvas.setPointerCapture(event.pointerId); } catch { }

    this.draggedPie = dragTarget;
    this.hoveredIndex = dragTarget.index;
    this.draw();
  };

  handlePointerMove = (event) => {
    event.preventDefault?.();

    // kalau ada pointer aktif, hanya proses pointer itu
    if (this.activePointerId !== null && event.pointerId !== this.activePointerId) return;

    const loc = getPointerLocation(event, this.canvas);

    // hover mode (belum drag)
    if (!this.draggedPie) {
      const hoveredTarget = this.getTarget(loc);
      if (hoveredTarget) {
        if (this.hoveredIndex !== hoveredTarget.index) {
          this.hoveredIndex = hoveredTarget.index;
          this.draw();
        }
      } else if (this.hoveredIndex !== -1) {
        this.hoveredIndex = -1;
        this.draw();
      }
      return;
    }

    // drag mode
    const draggedPie = this.draggedPie;
    const dx = loc.x - draggedPie.centerX;
    const dy = loc.y - draggedPie.centerY;

    const newAngle = Math.atan2(dy, dx) - draggedPie.angleOffset;
    this.shiftSelectedAngle(newAngle);
    this.draw();
  };

  handlePointerUp = (event) => {
    if (this.activePointerId === null) return;
    if (event.pointerId !== this.activePointerId) return;

    this.activePointerId = null;

    try {
      this.canvas.releasePointerCapture(event.pointerId);
    } catch { }

    if (this.draggedPie) {
      this.draggedPie = null;
      this.draw();
    }
  };

  handlePointerCancel = (event) => {
    // treat sama seperti pointerup
    this.handlePointerUp(event);
  };

  handlePointerLeave = (_event) => {
    // kalau tidak drag, hilangkan hover saat keluar canvas
    if (this.draggedPie) return;
    if (this.hoveredIndex !== -1) {
      this.hoveredIndex = -1;
      this.draw();
    }
  };

  constructor(setup) {
    this.canvas = setup.canvas;

    // penting: biar pointer drag di mobile nggak dianggap scroll/zoom
    this.canvas.style.touchAction = "none";

    const ctx = this.canvas.getContext("2d");
    if (!ctx) throw new Error("DraggablePiechart needs an html5 canvas.");
    this.context = ctx;

    // data
    if (setup.proportions) this.data = generateDataFromProportions(setup.proportions);
    else if (setup.data) this.data = setup.data;
    else throw new Error("DraggablePiechart requires `proportions` or `data`.");

    // options with fallback
    this.radius = setup.radius ?? DEFAULT_RADIUS;
    this.collapsing = setup.collapsing ?? DEFAULT_COLLAPSING;
    this.minAngle = setup.minAngle ?? DEFAULT_MIN_ANGLE;
    this.drawSegment = setup.drawSegment ?? defaultDrawSegment;
    this.drawNode = setup.drawNode ?? defaultDrawNode;
    this.onchange = setup.onchange ?? (() => { });

    // ✅ Pointer Events only
    this.canvas.addEventListener("pointerdown", this.handlePointerDown);
    this.canvas.addEventListener("pointermove", this.handlePointerMove);
    this.canvas.addEventListener("pointerup", this.handlePointerUp);
    this.canvas.addEventListener("pointercancel", this.handlePointerCancel);
    this.canvas.addEventListener("pointerleave", this.handlePointerLeave);

    this.draw();
  }

  destroy() {
    this.canvas.removeEventListener("pointerdown", this.handlePointerDown);
    this.canvas.removeEventListener("pointermove", this.handlePointerMove);
    this.canvas.removeEventListener("pointerup", this.handlePointerUp);
    this.canvas.removeEventListener("pointercancel", this.handlePointerCancel);
    this.canvas.removeEventListener("pointerleave", this.handlePointerLeave);
  }

  /* -------- API yang dipakai PieChart -------- */

  moveAngle(i, amount) {
    if (this.data[i].collapsed && amount < 0) {
      this.setCollapsed(i, false);
      return;
    }

    const geometry = this.getGeometry();
    this.draggedPie = {
      index: i,
      angleOffset: 0,
      centerX: geometry.centerX,
      centerY: geometry.centerY,
      startingAngles: this.data.map((v) => v.angle),
      collapsed: this.data.map((v) => v.collapsed),
      angleDragDistance: 0,
    };

    this.shiftSelectedAngle(this.data[i].angle + amount);
    this.draggedPie = null;
    this.draw();
  }

  getSliceSizePercentage(index) {
    const visibleSegments = this.getVisibleSegments();

    for (let i = 0; i < visibleSegments.length; i += 1) {
      if (visibleSegments[i].index === index) {
        return (100 * visibleSegments[i].arcSize) / TAU;
      }
    }
    return 0;
  }

  getAllSliceSizePercentages() {
    const visibleSegments = this.getVisibleSegments();
    const percentages = [];

    for (let i = 0; i < this.data.length; i += 1) {
      if (this.data[i].collapsed) {
        percentages[i] = 0;
      } else {
        for (let j = 0; j < visibleSegments.length; j += 1) {
          if (visibleSegments[j].index === i) {
            percentages[i] = (100 * visibleSegments[j].arcSize) / TAU;
          }
        }
      }
    }

    return percentages;
  }

  getGeometry() {
    const centerX = Math.floor(this.canvas.width / 2);
    const centerY = Math.floor(this.canvas.height / 2);
    return {
      centerX,
      centerY,
      radius: Math.min(centerX, centerY) * this.radius,
    };
  }

  getTarget(targetLocation) {
    const geometry = this.getGeometry();
    const startingAngles = [];
    const collapsed = [];

    const closest = {
      index: -1,
      distance: 9999999,
      angle: null,
    };

    for (let i = 0; i < this.data.length; i += 1) {
      startingAngles.push(this.data[i].angle);
      collapsed.push(this.data[i].collapsed);

      if (this.data[i].collapsed) continue;

      const dx = targetLocation.x - geometry.centerX;
      const dy = targetLocation.y - geometry.centerY;
      const trueGrabbedAngle = Math.atan2(dy, dx);

      const distance = Math.abs(
        smallestSignedAngleBetween(trueGrabbedAngle, this.data[i].angle)
      );

      if (distance < closest.distance) {
        closest.index = i;
        closest.distance = distance;
        closest.angle = trueGrabbedAngle;
      }
    }

    if (closest.distance < 0.1 && closest.angle !== null) {
      return {
        index: closest.index,
        angleOffset: smallestSignedAngleBetween(
          closest.angle,
          startingAngles[closest.index]
        ),
        centerX: geometry.centerX,
        centerY: geometry.centerY,
        startingAngles,
        collapsed,
        angleDragDistance: 0,
      };
    }
    return null;
  }

  setCollapsed(index, collapsed) {
    const setNewPos = this.data[index].collapsed && !collapsed;
    this.data[index].collapsed = collapsed;

    const visibleSegments = this.getVisibleSegments();

    for (let i = 0; i < visibleSegments.length; i += 1) {
      if (visibleSegments[i].index === index) {
        if (setNewPos) {
          const nextSegment =
            visibleSegments[mod(i + 1, visibleSegments.length)];
          this.data[index].angle = nextSegment.angle - this.minAngle;
        }

        for (let j = 0; j < visibleSegments.length - 1; j += 1) {
          const currentSegment =
            visibleSegments[mod(1 + i - j, visibleSegments.length)];
          const nextAlongSegment =
            visibleSegments[mod(i - j, visibleSegments.length)];

          const angleBetween = Math.abs(
            smallestSignedAngleBetween(
              this.data[currentSegment.index].angle,
              this.data[nextAlongSegment.index].angle
            )
          );

          if (angleBetween < this.minAngle) {
            this.data[nextAlongSegment.index].angle = normaliseAngle(
              this.data[currentSegment.index].angle - this.minAngle
            );
          }
        }
        break;
      }
    }

    this.draw();
  }

  getVisibleSegments() {
    const visibleSegments = [];

    for (let i = 0; i < this.data.length; i += 1) {
      if (!this.data[i].collapsed) {
        const startingAngle = this.data[i].angle;
        let foundNextAngle = false;

        for (let j = 1; j < this.data.length; j += 1) {
          const nextAngleIndex = (i + j) % this.data.length;

          if (!this.data[nextAngleIndex].collapsed) {
            let arcSize = this.data[nextAngleIndex].angle - startingAngle;
            if (arcSize <= 0) {
              arcSize += TAU;
            }

            visibleSegments.push({
              arcSize,
              angle: startingAngle,
              format: this.data[i].format,
              index: i,
            });

            foundNextAngle = true;
            break;
          }
        }

        if (!foundNextAngle) {
          visibleSegments.push({
            arcSize: TAU,
            angle: startingAngle,
            format: this.data[i].format,
            index: i,
          });
          break;
        }
      }
    }

    return visibleSegments;
  }

  getInvisibleSegments() {
    const invisibleSegments = [];
    for (let i = 0; i < this.data.length; i += 1) {
      if (this.data[i].collapsed) {
        invisibleSegments.push({
          index: i,
          format: this.data[i].format,
        });
      }
    }
    return invisibleSegments;
  }

  draw() {
    const context = this.context;
    const canvas = this.canvas;
    context.clearRect(0, 0, canvas.width, canvas.height);

    const geometry = this.getGeometry();
    const visibleSegments = this.getVisibleSegments();

    let largestArcSize = 0;
    let indexLargestArcSize = -1;

    for (let i = 0; i < visibleSegments.length; i += 1) {
      if (visibleSegments[i].arcSize > largestArcSize) {
        largestArcSize = visibleSegments[i].arcSize;
        indexLargestArcSize = i;
      }
    }

    for (let i = 0; i < visibleSegments.length; i += 1) {
      const index = mod(i + indexLargestArcSize + 1, visibleSegments.length);
      const seg = visibleSegments[index];
      this.drawSegment(
        context,
        this,
        geometry.centerX,
        geometry.centerY,
        geometry.radius,
        seg.angle,
        seg.arcSize,
        seg.format,
        false
      );
    }

    const invisibleSegments = this.getInvisibleSegments();
    for (let i = 0; i < invisibleSegments.length; i += 1) {
      const seg = invisibleSegments[i];
      this.drawSegment(
        context,
        this,
        geometry.centerX,
        geometry.centerY,
        geometry.radius,
        0,
        0,
        seg.format,
        true
      );
    }

    for (let i = 0; i < visibleSegments.length; i += 1) {
      const seg = visibleSegments[i];
      const location = polarToCartesian(seg.angle, geometry.radius);
      this.drawNode(
        context,
        this,
        location.x,
        location.y,
        geometry.centerX,
        geometry.centerY,
        i === this.hoveredIndex
      );
    }

    this.onchange(this);
  }

  shiftSelectedAngle(newAngle) {
    if (!this.draggedPie) return;
    const draggedPie = this.draggedPie;

    const startingAngle = draggedPie.startingAngles[draggedPie.index];

    let angleDragDistance = smallestSignedAngleBetween(newAngle, startingAngle);
    let previousDragDistance = draggedPie.angleDragDistance;

    let rotationDirection = previousDragDistance > 0 ? 1 : -1;

    const sameDirection =
      (previousDragDistance > 0) === (angleDragDistance > 0);
    const greaterThanHalf =
      Math.abs(previousDragDistance - angleDragDistance) > Math.PI;

    if (greaterThanHalf && !sameDirection) {
      angleDragDistance =
        (TAU - Math.abs(angleDragDistance)) * rotationDirection;
    } else {
      rotationDirection = angleDragDistance > 0 ? 1 : -1;
    }

    draggedPie.angleDragDistance = angleDragDistance;

    this.data[draggedPie.index].angle = normaliseAngle(
      startingAngle + angleDragDistance
    );

    this.data[draggedPie.index].collapsed =
      draggedPie.collapsed[draggedPie.index];

    let shifting = true;
    let collapsed = false;
    const minAngle = this.minAngle;
    let numberOfAnglesShifted = 0;

    for (let i = 1; i < this.data.length; i += 1) {
      const index = mod(
        draggedPie.index + i * rotationDirection,
        this.data.length
      );

      let startingAngleToNonDragged = smallestSignedAngleBetween(
        draggedPie.startingAngles[index],
        startingAngle
      );

      if (startingAngleToNonDragged * rotationDirection < 0) {
        startingAngleToNonDragged =
          (startingAngleToNonDragged * rotationDirection + TAU) *
          rotationDirection;
      }

      if (this.collapsing) {
        this.data[index].collapsed = draggedPie.collapsed[index];

        const checkForSnap = !collapsed && !this.data[index].collapsed;

        if (
          checkForSnap &&
          startingAngleToNonDragged > 0 &&
          angleDragDistance > startingAngleToNonDragged - minAngle
        ) {
          this.data[draggedPie.index].angle = this.data[index].angle;
          this.data[draggedPie.index].collapsed = true;
          collapsed = true;
        } else if (
          checkForSnap &&
          startingAngleToNonDragged < 0 &&
          angleDragDistance < startingAngleToNonDragged + minAngle
        ) {
          this.data[draggedPie.index].angle = this.data[index].angle;
          this.data[index].collapsed = true;
          collapsed = true;
        } else {
          this.data[index].angle = draggedPie.startingAngles[index];
        }
      } else {
        const shift = (numberOfAnglesShifted + 1) * minAngle;

        if (
          shifting &&
          startingAngleToNonDragged > 0 &&
          angleDragDistance > startingAngleToNonDragged - shift
        ) {
          this.data[index].angle = normaliseAngle(
            draggedPie.startingAngles[index] +
            (angleDragDistance - startingAngleToNonDragged) +
            shift
          );
          numberOfAnglesShifted += 1;
        } else if (
          shifting &&
          startingAngleToNonDragged < 0 &&
          angleDragDistance < startingAngleToNonDragged + shift
        ) {
          this.data[index].angle = normaliseAngle(
            draggedPie.startingAngles[index] -
            (startingAngleToNonDragged - angleDragDistance) -
            shift
          );
          numberOfAnglesShifted += 1;
        } else {
          shifting = false;
          this.data[index].angle = draggedPie.startingAngles[index];
        }
      }
    }
  }
}
